<template>
  <div
    v-if="cart.cart.length > 0 && targetingLogicHTML != ''"
    id="map-targeting-summary">
    <div class="formula-container mt-2">
      <div @click="copyToClipboard(onCopyID)">
        <icon-copy
          v-tooltip.top="{
            content: $t('messages.clickToCopyToTheClipboard'),
            delay: 200,
          }" />
      </div>
      <div
        v-html="targetingLogicHTML"
        class="well formula dark" />
    </div>
    <div class="badge badge-rounded badge-default badge-inverse">
      {{ uppercase($t('names.targetingSummary', { n: 1 })) }}
    </div>
  </div>
</template>

<script>
  import DOMPurify from 'dompurify';

  import { IconCopy } from '@/common/icons/monochrome';
  import { copyToClipboard, uppercase } from '@/globals';
  import UserNotifications from '@/utils/UserNotifications';
  import i18n from '@/vueI18n';

  import TargetingLogic from './TargetingLogic';

  export default {
    components: {
      IconCopy,
    },
    props: {
      cart: {
        type: Object,
        required: true,
      },
      params: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        targetingLogic: new TargetingLogic(),
      };
    },
    computed: {
      targetingLogicHTML() {
        return DOMPurify.sanitize(this.targetingLogic.targetingLogicHTML, { USE_PROFILES: { html: true } });
      },
    },
    watch: {
      params() {
        this.targetingLogic.add(this.cart);
      },
      targetingLogicHTML(val) {
        this.$emit('onChange', val !== '');
      },
    },
    mounted() {
      this.targetingLogic.add(this.cart);
    },
    methods: {
      uppercase,
      onCopyID() {
        UserNotifications.success({
          title: i18n.global.t('messages.copyTargetingLogic'),
          message: i18n.global.t('messages.copiedTargetingLogicToClipboard'),
        });
      },
      copyToClipboard(successCallback) {
        const targetingLogic = this.targetingLogicHTML
          .replaceAll(', ...', '')
          .replaceAll(/<div class="full-list".*?>.*?<\/div>/gim, '')
          .replaceAll(/<[^>]*>/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        copyToClipboard(targetingLogic, successCallback);
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss">
  @use '@/styles/variables' as *;

  #view-summary {
    display: flex;
    align-items: center;
    margin-right: 2.3rem;
    margin-bottom: 0;

    .checkbox-container {
      display: block;
      position: relative;
      cursor: pointer;
      font-size: 2.2rem;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      height: 2rem;
      width: 2rem;
      margin-right: 0.7rem;

      input#summary-filter {
        opacity: 0;
        position: absolute;
      }

      input:checked ~ .checkmark {
        background-color: #00e074;
        box-shadow: 0 0.1rem 0.3rem #00000020;

        &:after {
          content: '';
          position: absolute;
          left: 0.7rem;
          top: 0.4rem;
          width: 0.5rem;
          height: 1rem;
          border: solid white;
          border-width: 0 0.3rem 0.3rem 0;
          transform: rotate(45deg);
        }
      }

      .checkmark {
        position: absolute;
        width: 2rem;
        height: 2rem;
        background-color: #eee;
        border-radius: 0.4rem;
        box-shadow: inset 0 0.1rem 0.1rem 0 rgba(0, 0, 0, 0.1);
      }
    }

    .group-label {
      text-transform: uppercase;
      font-size: 0.7em;
      font-weight: 500;
    }
  }

  #map-targeting-summary {
    background: #fff;
    padding: 2rem;
    border-bottom: solid 0.1rem $borders;
    position: relative;

    svg {
      stroke: #fff;
      width: 1.8rem;
      margin-right: 0.6rem;
      stroke-width: 0.15rem;
      flex-shrink: 0;
      position: absolute;
      right: 0.7rem;
      top: 1rem;
      z-index: 1;
      cursor: pointer;
    }

    .badge {
      position: absolute;
      top: 1rem;
      margin-left: -0.6rem;
    }

    .formula-container {
      position: relative;
      padding-top: 0.8rem;
      padding-bottom: 0.8rem;
      border-radius: 0.8rem;
      max-height: 19.5rem;
      margin: 2rem 0 0;

      background: var(--info);
    }

    .well.formula {
      font-family: $monospace-font-family;
      transition: box-shadow 0.2s ease-in-out;
      font-size: 1.3rem;
      border: none;
      border-radius: 0.8rem;
      margin-bottom: 0;
      box-shadow: none;
      max-height: 18.5rem;
      padding: 1rem 3.3rem 0 1.6rem;
      overflow-y: auto;
      position: unset;

      background: var(--info);
      color: var(--infoReverse);

      span {
        display: inline-block;
        margin-bottom: 1.2rem;

        &.boolean-expression {
          margin-bottom: 0;
        }
      }

      .rule {
        background-color: rgba(255, 255, 255, 0.2);
        padding: 0.3rem 0.8rem;
        border-radius: 0.6rem;
        transition: all 0.2s ease-in-out;
      }

      .group-filter {
        display: unset;
        overflow-wrap: anywhere;
        position: unset;
        &.hoverable {
          cursor: pointer;
        }
        &.hoverable:hover {
          .rule {
            color: var(--info);

            background: #ffffff;
          }
          .full-list {
            display: flex;
            background: rgba(0, 0, 0, 0.8);
            transition: all 1s ease-in-out;
          }
        }

        .full-list {
          display: none;
          position: absolute;
          z-index: 3;
          bottom: -3.1rem;
          right: 1.5rem;
          background: black;
          padding: 1.5rem;
          border-radius: 1rem;
          max-width: 60rem;
          min-width: 20rem;
          width: 100%;
        }

        // limitOfElementsPerGroup = 5 (* 2 for ',')) = 10;
        span:nth-child(n + 10) {
          display: none;
        }
      }

      .filter-type {
        font-weight: bold;
      }

      .operand {
        text-transform: lowercase;
      }
    }
  }
</style>
